import type { TractorWithAxlesResponse } from '@contracts/tractor/TractorWithAxlesResponse';
import { MenuItem, Select } from '@src/common/components/Select';
import { css } from '@src/common/styles/Styles';
import { useTractors } from '@src/tractor/hooks/useTractors';
import type { Ref } from 'react';
import { forwardRef } from 'react';
import type { ControllerRenderProps, FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props extends Partial<ControllerRenderProps> {
  clientId: string;
  error?: FieldError;
  defaultValue?: { label: string; value: string };
  onTractorSelect: (tractor: TractorWithAxlesResponse | undefined) => void;
}

export const newTractorKey = 'new';

export const TractorSelector = forwardRef(function CustomInput(
  { clientId, onChange, onTractorSelect, defaultValue, error, ...props }: Props,
  ref: Ref<HTMLDivElement>,
) {
  const { tractors, isLoading } = useTractors(clientId);
  const { t } = useTranslation();

  const selectOptions = [
    { label: t('newTractor'), value: newTractorKey },
    ...(isLoading
      ? defaultValue
        ? [defaultValue]
        : []
      : tractors.tractors.map((tractor) => ({
          value: tractor.id,
          label: tractor.unitNumber,
        }))),
  ];

  return (
    <Select
      ref={ref}
      {...props}
      onChange={(e) => {
        onChange?.(e);
        onTractorSelect(
          tractors.tractors.find(
            (tractor) => tractor.id === (e.target.value as string),
          ),
        );
      }}
      disabled={isLoading || props.disabled}
      value={props.value || defaultValue?.value || 'new'}
      error={error && t(`formErrors.${error.message}`)}
      className={fullWidth}
      label={t(`tractors`)}
    >
      {selectOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
});

const fullWidth = css({
  flex: 1,
});
